import { oc } from "@orpc/contract";
import { z } from "zod";
import { PhotoContentType } from "./product";

/**
 * Bedrijfsgegevens (Instellingen → Bedrijfsgegevens, spec "Orders, facturen en mail"): the
 * company behind the shop, as every invoice states it. One set; until it is saved the shop's
 * known details apply (`version` 0). An invoice copies them when it is issued, so a change applies
 * to the next invoice only.
 */

const text = (max: number) => z.string().trim().min(1).max(max);
/** Optional text: empty becomes `null`. */
const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.nullable()
		.transform((value) => (value === "" ? null : value));

/** An IBAN as printed: spaces allowed, checked on its shape (country, check digits, account). */
export const Iban = z
	.string()
	.trim()
	.transform((value) => value.replace(/\s+/g, "").toUpperCase())
	.pipe(z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/));

export const CompanyDetailsFields = z.object({
	/** The legal name. */
	name: text(120),
	/** The name the shop trades under, shown large on the invoice; empty: only the legal name. */
	tradeName: optionalText(120),
	/** Street and house number. */
	street: text(120),
	postcode: text(12),
	city: text(80),
	country: text(60),
	kvk: z
		.string()
		.trim()
		.regex(/^\d{8}$/),
	vatId: z
		.string()
		.trim()
		.transform((value) => value.replace(/\s+/g, "").toUpperCase())
		.pipe(z.string().regex(/^[A-Z]{2}[A-Z0-9]{8,12}$/)),
	iban: Iban,
	/** For the invoice's footer. */
	email: z
		.union([z.literal(""), z.email().max(254)])
		.nullable()
		.transform((value) => (value === "" ? null : value)),
	phone: optionalText(30),
	website: optionalText(120),
});
export type CompanyDetailsFields = z.infer<typeof CompanyDetailsFields>;
export type CompanyDetailsInput = z.input<typeof CompanyDetailsFields>;

export const CompanyLogo = z.object({
	contentType: PhotoContentType,
	/** Where the admin shows it (`/media/company/...`); may be root-relative. */
	url: z.string(),
});
export type CompanyLogo = z.infer<typeof CompanyLogo>;

/**
 * The details as stored. Plain strings, not the save's checks: the defaults that apply before the
 * first save have no IBAN yet (empty), and the form shows what is there.
 */
export const CompanyDetails = z.object({
	name: z.string(),
	tradeName: z.string().nullable(),
	street: z.string(),
	postcode: z.string(),
	city: z.string(),
	country: z.string(),
	kvk: z.string(),
	vatId: z.string(),
	/** Empty until it is saved. */
	iban: z.string(),
	email: z.string().nullable(),
	phone: z.string().nullable(),
	website: z.string().nullable(),
	logo: CompanyLogo.nullable(),
	/** Up by one on every save; 0 while the defaults apply. */
	version: z.number().int().min(0),
});
export type CompanyDetails = z.infer<typeof CompanyDetails>;

/** A save: the details and the `version` they were loaded at; a stale save is refused (409). */
export const CompanyDetailsUpdate = CompanyDetailsFields.extend({ version: z.number().int().min(0) });
export type CompanyDetailsUpdate = z.infer<typeof CompanyDetailsUpdate>;

/** The largest logo: 2 MB (it is embedded in every invoice pdf). */
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/**
 * What the shop shows of the company (footer, contact page; frontend spec "Aanvullingen op de
 * backend", point 3): who sells, where, and how to reach customer service. No IBAN, no logo, no
 * version: those are for invoices and the admin.
 */
export const ShopCompany = CompanyDetails.pick({
	name: true,
	tradeName: true,
	street: true,
	postcode: true,
	city: true,
	country: true,
	kvk: true,
	vatId: true,
	/** Customer service; `null` until it is filled in. */
	email: true,
	phone: true,
});
export type ShopCompany = z.infer<typeof ShopCompany>;

export const CompanyDetailsContract = {
	get: oc.route({ method: "GET", path: "/admin/company-details" }).output(CompanyDetails),
	/** Errors: `CompanyDetailsChanged` (409): someone saved meanwhile; load them again. */
	update: oc
		.route({ method: "PUT", path: "/admin/company-details" })
		.input(CompanyDetailsUpdate)
		.output(CompanyDetails),
	/**
	 * Replaces the logo. The body is the file itself (JPEG, PNG, WebP or AVIF, at most
	 * `MAX_LOGO_BYTES`), sent as a `File`. Errors: `NotAnImage` (415), 413 when too large.
	 */
	uploadLogo: oc
		.route({ method: "POST", path: "/admin/company-details/logo", inputStructure: "detailed" })
		.input(z.object({ body: z.file() }))
		.output(CompanyDetails),
	/** Invoices from now on have no logo; issued invoices keep theirs. */
	deleteLogo: oc.route({ method: "DELETE", path: "/admin/company-details/logo" }).output(CompanyDetails),
	/** Public and cacheable (a few minutes): the shop's footer and contact page. */
	shop: oc.route({ method: "GET", path: "/shop/company" }).output(ShopCompany),
};
